// ---------------------------------------------------------------------------
// Prompt attachment helpers (pure, no React).
//
// Shared by every "Ask AI" prompt surface. Reads File/Blob objects into the
// canonical `Attachment` shape, classifies text-vs-image, enforces client-side
// caps, and strips client-only fields before sending (`toWire`).
//
// Caps here are SOFT pre-checks for a good UX; the backend re-validates and is
// authoritative (it may reject with a 400 / WS error which the UI surfaces).
// ---------------------------------------------------------------------------
import type { Attachment, AttachmentKind } from '@/types'

export const MAX_ATTACHMENTS = 10
export const MAX_TEXT_BYTES = 256 * 1024            // 256 KB per text file
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024      // 5 MB per image
export const MAX_DOC_BYTES = 10 * 1024 * 1024       // 10 MB per PDF/DOCX
export const MAX_SHEET_BYTES = 10 * 1024 * 1024     // 10 MB per CSV/Excel data file
export const MAX_DESIGN_BYTES = 15 * 1024 * 1024    // 15 MB per Claude Design export (.zip)
// Total raw image bytes per send. Kept conservative so a single WebSocket
// `message` (base64 is ~1.34x raw + JSON overhead) stays under uvicorn's
// default 16 MB WS frame limit. See the plan's transport-size note.
export const MAX_TOTAL_IMAGE_BYTES = 9 * 1024 * 1024
// Total raw bytes across ALL binary attachments (images + documents). 18 MB
// raw ≈ 24 MB base64, which fits under the backend's MAX_TOTAL_B64_BYTES
// (25 MB) and the 32 MB ws_max_size set in backend/app/main.py.
export const MAX_TOTAL_BINARY_BYTES = 18 * 1024 * 1024
// Pasted text at/above this length becomes a "Pasted text" chip instead of
// flooding the textarea (Claude-Code-style UX).
export const LARGE_PASTE_THRESHOLD = 8000

const IMAGE_MIME_RE = /^image\/(png|jpe?g|gif|webp)$/i

export const PDF_MIME = 'application/pdf'
export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// File input accept attribute — images + documents + spreadsheets + common
// text/code extensions.
export const ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,' +
  `${PDF_MIME},.pdf,${DOCX_MIME},.docx,` +
  `${XLSX_MIME},.xlsx,.xlsm,.xls,.ods,.csv,.tsv,` +
  '.txt,.md,.markdown,.json,.jsonc,.log,.yml,.yaml,.toml,.ini,.env,' +
  '.js,.jsx,.ts,.tsx,.mjs,.cjs,.py,.rb,.go,.rs,.java,.kt,.c,.h,.cpp,.hpp,.cc,' +
  '.cs,.php,.swift,.sql,.sh,.bash,.zsh,.html,.htm,.css,.scss,.sass,.less,.xml,' +
  '.vue,.svelte,.graphql,.gql,.proto'

// The Ask-AI dock also takes a Claude Design export (.zip). Other prompt
// surfaces do not import designs, so they keep ATTACHMENT_ACCEPT.
export const ATTACHMENT_ACCEPT_WITH_DESIGN = `${ATTACHMENT_ACCEPT},.zip,application/zip`

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp'])
// Documents the BACKEND can extract text from (pypdf / python-docx). Legacy
// `.doc` stays in BINARY_EXT — neither library reads it.
const DOC_EXT = new Set(['pdf', 'docx'])
const DOC_MIME = new Set([PDF_MIME, DOCX_MIME])
// Tabular DATA files — sent as raw bytes so the backend reads the encoding and
// the workbook itself (the browser can't parse Excel), and stages every row
// for import instead of truncating it into the prompt.
const SHEET_EXT = new Set(['csv', 'tsv', 'tab', 'xlsx', 'xlsm', 'xls', 'ods'])
const SHEET_MIME = new Set([
  XLSX_MIME,
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
  'text/tab-separated-values',
])
const TEXT_EXT = new Set([
  'txt', 'md', 'markdown', 'json', 'jsonc', 'log', 'yml', 'yaml',
  'toml', 'ini', 'env', 'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs', 'py', 'rb', 'go',
  'rs', 'java', 'kt', 'c', 'h', 'cpp', 'hpp', 'cc', 'cs', 'php', 'swift', 'sql',
  'sh', 'bash', 'zsh', 'html', 'htm', 'css', 'scss', 'sass', 'less', 'xml', 'vue',
  'svelte', 'graphql', 'gql', 'proto', 'dockerfile', 'makefile', 'gitignore', 'tex',
])
// Obvious binaries we reject up-front for a clearer message.
const BINARY_EXT = new Set([
  'zip', 'gz', 'tar', 'rar', '7z', 'exe', 'dll', 'so', 'dylib', 'bin', 'wasm',
  'o', 'a', 'mp3', 'mp4', 'mov', 'avi', 'mkv', 'wav', 'flac', 'ogg', 'webm', 'doc',
  'ppt', 'pptx', 'psd', 'ai', 'sketch', 'ttf', 'otf', 'woff',
  'woff2', 'ico', 'bmp', 'tiff', 'tif', 'heic', 'svg',
])

function extOf(name: string): string {
  const i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i + 1).toLowerCase() : ''
}

/** Decide whether a file is a supported text or image, or `null` (rejected).
 *  `allowDesign` admits a Claude Design export (.zip) — only the Ask-AI dock,
 *  whose backend imports it, passes it. */
export function classifyKind(file: File, opts?: { allowDesign?: boolean }): AttachmentKind | null {
  const mime = (file.type || '').toLowerCase()
  const ext = extOf(file.name)
  if (opts?.allowDesign && ext === 'zip') return 'design'
  if (IMAGE_MIME_RE.test(mime) || IMAGE_EXT.has(ext)) return 'image'
  if (DOC_MIME.has(mime) || DOC_EXT.has(ext)) return 'document'
  // Extension first: Windows reports a .csv as application/vnd.ms-excel.
  if (SHEET_EXT.has(ext) || (!ext && SHEET_MIME.has(mime))) return 'spreadsheet'
  if (BINARY_EXT.has(ext)) return null
  if (mime.startsWith('text/')) return 'text'
  if (
    mime === 'application/json' ||
    mime === 'application/xml' ||
    mime === 'application/javascript' ||
    mime === 'application/x-yaml' ||
    mime === 'application/sql'
  ) {
    return 'text'
  }
  if (TEXT_EXT.has(ext)) return 'text'
  // No MIME and no extension (e.g. clipboard text blob) → treat as text.
  if (!mime && !ext) return 'text'
  return null
}

/** Stable id with a fallback for non-secure contexts where randomUUID is absent. */
export function makeId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `att_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Document MIME derived from the filename, for browsers that report none. */
function mimeForDoc(name: string): string {
  return extOf(name) === 'docx' ? DOCX_MIME : PDF_MIME
}

/** Read a File into an `Attachment`. Rejects unsupported types / read errors. */
export function readFileAsAttachment(
  file: File,
  opts?: { allowDesign?: boolean },
): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const kind = classifyKind(file, opts)
    if (!kind) {
      reject(new Error(`Unsupported file type: ${file.name || 'unnamed'}`))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`Failed to read ${file.name || 'file'}`))
    if (kind === 'image' || kind === 'document' || kind === 'spreadsheet' || kind === 'design') {
      reader.onload = () => {
        const result = String(reader.result || '')
        // Strip the "data:<mime>;base64," prefix → raw base64 in data_b64.
        const comma = result.indexOf(',')
        const data_b64 = comma >= 0 ? result.slice(comma + 1) : result
        resolve({
          id: makeId(),
          kind,
          name:
            file.name ||
            (kind === 'document' ? 'document.pdf' : kind === 'spreadsheet' ? 'data.csv' : 'pasted-image.png'),
          // The backend validates documents on MIME, and Safari/Windows often
          // report an empty file.type for .docx — derive it from the extension.
          // Spreadsheets are validated on their extension, so any MIME will do.
          mime:
            (kind === 'design' ? 'application/zip' : '') ||
            file.type ||
            (kind === 'document'
              ? mimeForDoc(file.name)
              : kind === 'spreadsheet'
                ? 'application/octet-stream'
                : 'image/png'),
          size: file.size,
          data_b64,
        })
      }
      reader.readAsDataURL(file)
    } else {
      reader.onload = () => {
        resolve({
          id: makeId(),
          kind: 'text',
          name: file.name || 'pasted.txt',
          mime: file.type || 'text/plain',
          size: file.size,
          text: String(reader.result || ''),
        })
      }
      reader.readAsText(file)
    }
  })
}

/** Build a text attachment from a (large) pasted string. */
export function makeTextAttachment(text: string, name = 'Pasted text'): Attachment {
  return {
    id: makeId(),
    kind: 'text',
    name,
    mime: 'text/plain',
    size: new Blob([text]).size,
    text,
  }
}

// What actually goes over the wire — `id`/`size` stripped.
export interface WireAttachment {
  name: string
  kind: AttachmentKind
  mime: string
  text?: string
  data_b64?: string
}

export function toWire(att: Attachment): WireAttachment {
  // Images, documents and spreadsheets travel as raw base64; documents are
  // extracted to text and spreadsheets parsed server-side.
  return att.kind === 'text'
    ? { name: att.name, kind: 'text', mime: att.mime, text: att.text }
    : { name: att.name, kind: att.kind, mime: att.mime, data_b64: att.data_b64 }
}
